// TeamsReassignOnDeletePage.jsx
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import ReactTooltip from "react-tooltip";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumbs from "../../components/Breadcrumbs";
import Header from "../../components/Header";
import PageContainer from "../../components/PageContainer";
import Sidebar from "../../components/Sidebar";
import { usePermissions } from "../../context/PermissionsContext";
import { deleteTeamDoAll } from "../../services/teams/deleteTeamDoAll";
import { getReassignPreview } from "../../services/teams/getTeamsPreview";
import ConfirmationModal from "../../components/Modals/ConfirmationModal";
import ReassignTeamTable from "./ReassignTeamTable";

function TeamSelect({
    teams,
    value,
    onChange,
    excludeId,
    placeholder = "Select team…",
    disabled = false,
}) {
    const options = excludeId ? teams.filter((t) => t.id !== excludeId) : teams;
    return (
        <select
            value={value || ""}
            onChange={(e) => onChange(e.target.value || "")}
            disabled={disabled || options.length === 0}
            className="block w-full px-2 py-2 text-sm border rounded-sm outline-none border-slate-300 focus:ring-0 focus:border-teal-500 disabled:bg-slate-50 disabled:text-slate-400"
            title={
                disabled || options.length === 0
                    ? "No candidate teams available"
                    : ""
            }
        >
            <option value="">{placeholder}</option>
            {options.map((t) => (
                <option key={t.id} value={t.id}>
                    {t.name}
                </option>
            ))}
        </select>
    );
}

function BulkActionBar({ count, teams, targetId, setTargetId, onApply, disabled }) {
    if (count === 0) return null;

    const noCandidates = (teams?.length ?? 0) === 0;
    const isDisabled = disabled || noCandidates || !targetId;

    return (
        <div className="sticky z-40 flex justify-center w-full px-4 pointer-events-none bottom-4">
            <div className="w-full max-w-5xl border shadow-2xl pointer-events-auto rounded-xl border-slate-900/70 bg-slate-900/95 text-slate-50 shadow-black/40">
                <div className="flex flex-col gap-4 p-4 md:flex-row md:items-center md:justify-between">
                    {/* Left side: label + count */}
                    <div className="flex items-center gap-3 text-sm">
                        <span className="inline-flex items-center rounded-full bg-slate-800 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-200">
                            Bulk actions
                        </span>
                        <span>
                            <span className="font-semibold">{count}</span> selected
                        </span>
                    </div>

                    {/* Right side: select + button */}
                    <div className="flex flex-col items-stretch gap-3 md:flex-row md:items-center">
                        <div className="min-w-[260px] [&_*]:!text-slate-100 [&_*]:!bg-slate-800 [&_*]:!border-slate-700">
                            <TeamSelect
                                teams={teams}
                                value={targetId}
                                onChange={setTargetId}
                                placeholder={
                                    noCandidates
                                        ? "No candidate teams"
                                        : "Assign to team…"
                                }
                                disabled={disabled || noCandidates}
                            />
                        </div>

                        <button
                            type="button"
                            onClick={onApply}
                            disabled={isDisabled}
                            className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium transition-colors bg-teal-400 rounded-md shadow-md text-slate-900 hover:bg-teal-300 disabled:opacity-60 disabled:hover:bg-teal-400"
                        >
                            Apply to selected
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

const TeamsReassignOnDeletePage = () => {
    const { can, permissionsLoading } = usePermissions();
    const navigate = useNavigate();
    const params = useParams();
    const routeTeamId = params?.id ?? null;

    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [errorStatus, setErrorStatus] = useState(null);
    const [deletionNeedsReview, setDeletionNeedsReview] = useState(false);

    const [previewReady, setPreviewReady] = useState(false);
    const [previewAttempt, setPreviewAttempt] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const inFlight = useRef(false);

    // Meta coming from preview API
    const [teamIdToDelete, setTeamIdToDelete] = useState(routeTeamId);
    const [departmentName, setDepartmentName] = useState("");
    const [teamName, setTeamName] = useState("");
    const [, setDepartmentId] = useState(null);

    const [teams, setTeams] = useState([]); // candidateTeams
    const [clients, setClients] = useState([]); // affectedClients

    // Draft targets become the final payload only on deletion; selection scopes bulk edits.
    const [targetByClient, setTargetByClient] = useState({});
    const [selected, setSelected] = useState(new Set());
    const [visibleRows, setVisibleRows] = useState([]);

    // Bulk
    const [bulkTarget, setBulkTarget] = useState("");
    const noPermissionMessage = "You don't have permission to do this.";
    const canReadTeams = can("teams.read");
    const canDeleteTeams = can("teams.delete");

    // Load data from preview (single call)
    useEffect(() => {
        setPreviewReady(false);
        setClients([]);
        setTeams([]);
        setTargetByClient({});
        setSelected(new Set());
        setConfirmOpen(false);
        if (permissionsLoading || !canReadTeams || !canDeleteTeams) {
            setLoading(permissionsLoading);
            return;
        }

        let mounted = true;
        (async () => {
            try {
                setLoading(true);
                setError(null);
                setErrorStatus(null);
                setDeletionNeedsReview(false);

                if (
                    !routeTeamId ||
                    routeTeamId === "null" ||
                    routeTeamId === "undefined"
                ) {
                    setError("Missing Team id in route.");
                    return;
                }

                const previewData = await getReassignPreview(routeTeamId);
                const preview = previewData.data;
                if (!mounted) return;
                if (!previewData.success || preview?.success === false) {
                    setErrorStatus(previewData.status);
                    setError(previewData.message || "Reassignment preview is unavailable.");
                    return;
                }
                if (preview?.teamId !== routeTeamId || !Array.isArray(preview.candidateTeams) || !Array.isArray(preview.affectedClients)
                    || preview.candidateTeams.some((t) => !t.id || t.id === routeTeamId)
                    || preview.affectedClients.some((c) => !c.id)) {
                    setError("The server returned an incomplete preview. Reload before continuing.");
                    return;
                }
                setPreviewReady(true);
                setTeamIdToDelete(preview.teamId);
                setDepartmentId(preview.departmentId ?? null);
                setDepartmentName(preview.departmentName ?? "");
                setTeamName(preview.teamName ?? "");

                setTeams(preview.candidateTeams || []);

                // 5) Clients: mapea affectedClients
                const ZERO = "00000000-0000-0000-0000-000000000000";
                const clientsFromApi = (preview.affectedClients || []).map((c) => ({
                    id: c.id,
                    name: c.name,
                    email: c.email || "",
                    manager: c.manager || "",
                    currentTeamId:
                        !c.currentTeamId || c.currentTeamId === ZERO
                            ? null
                            : c.currentTeamId,
                }));
                setClients(clientsFromApi);

                // 3) Targets por fila vacíos
                const init = {};
                clientsFromApi.forEach((c) => (init[c.id] = ""));
                setTargetByClient(init);
            } catch (e) {
                if (mounted) setError("Could not load the reassignment preview. Please try again later.");
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => {
            mounted = false;
        };
    }, [routeTeamId, canReadTeams, canDeleteTeams, permissionsLoading, previewAttempt]);

    // Helpers
    const currentTeamName = useCallback(
        (teamId) => {
            if (!teamId) return "Unassigned";
            if (teamId === teamIdToDelete) return "Team to delete";
            const t = teams.find((t) => t.id === teamId);
            return t ? t.name : "—";
        },
        [teams, teamIdToDelete]
    );

    // Derived data to keep columns stable
    const tableData = useMemo(
        () =>
            clients.map((c) => ({
                ...c,
                isSelected: selected.has(c.id),
                targetTeamId: targetByClient[c.id] || "",
            })),
        [clients, selected, targetByClient]
    );

    // Selection handlers
    const toggleOne = useCallback((id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    }, []);

    const toggleAllVisible = useCallback(() => {
        const visibleIds = visibleRows.map((r) => r.id);
        const allSelected =
            visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
        setSelected((prev) => {
            const next = new Set(prev);
            if (allSelected) {
                visibleIds.forEach((id) => next.delete(id));
            } else {
                visibleIds.forEach((id) => next.add(id));
            }
            return next;
        });
    }, [visibleRows, selected]);

    const areAllVisibleSelected =
        visibleRows.length > 0 && visibleRows.every((r) => selected.has(r.id));

    // Row actions (mocked por ahora)
    const updateClientTarget = useCallback((clientId, teamId) => {
        setTargetByClient((prev) => ({ ...prev, [clientId]: teamId }));
    }, []);

    const applyBulk = useCallback(() => {
        if (!previewReady || submitting || !canDeleteTeams || !teams.some((t) => t.id === bulkTarget)) return;
        setTargetByClient((prev) => {
            const next = { ...prev };
            selected.forEach((id) => { next[id] = bulkTarget; });
            return next;
        });
        toast.success("Assignments ready. Confirm deletion to save changes.");
    }, [selected, bulkTarget, teams, previewReady, submitting, canDeleteTeams]);

    const stillInDeletedTeam = clients.some((c) => {
        const target = targetByClient[c.id];
        return !teams.some((t) => t.id === target);
    });
    const canSubmit = !permissionsLoading && canReadTeams && canDeleteTeams && previewReady && teamIdToDelete === routeTeamId && !loading && !submitting && (!error || errorStatus === 400 || errorStatus === 422) && !stillInDeletedTeam;

    const onConfirmDelete = async () => {
        if (!canSubmit || inFlight.current) return;
        inFlight.current = true;
        setSubmitting(true);
        try {
            const mappings = clients.map((c) => ({ clientId: c.id, targetTeamId: targetByClient[c.id] }));
            const res = await deleteTeamDoAll(teamIdToDelete, {
                mappings, dryRun: false,
            });
            setConfirmOpen(false);
            if (res.success && res.data?.success === true) {
                toast.success("Team deleted; affected clients and subscriptions reassigned.");
                navigate("/teams");
            } else {
                // Only known validation rejections allow editing and resubmitting this draft.
                // A 409 can represent an uncertain commit, so it always requires review.
                const validationFailure = res.status === 400 || res.status === 422;
                setDeletionNeedsReview(!validationFailure);
                setErrorStatus(res.status);
                setError(validationFailure
                    ? "Deletion was rejected. Review the replacement assignments and try again."
                    : res.message || "The deletion result could not be confirmed. It may have completed.");
                setPreviewReady(validationFailure);
            }
        } catch {
            setConfirmOpen(false);
            setDeletionNeedsReview(true);
            setErrorStatus(null);
            setError("The deletion result could not be confirmed. It may have completed.");
            setPreviewReady(false);
        } finally {
            inFlight.current = false;
            setSubmitting(false);
        }
    };

    const noCandidates = (teams?.length ?? 0) === 0;

    // Columns (keep dependencies minimal)
    const columns = useMemo(
        () => [
            {
                Header: (
                    <div className="flex items-center">
                        <input
                            type="checkbox"
                            aria-label="Select all visible clients"
                            className="text-teal-600 rounded-sm border-slate-300 focus:ring-0"
                            checked={areAllVisibleSelected}
                            onChange={toggleAllVisible}
                        />
                    </div>
                ),
                id: "select",
                disableSortBy: true,
                width: 48,
                minWidth: 48,
                maxWidth: 48,
                Cell: ({ row }) => {
                    const id = row.original.id;

                    return (
                        <input
                            type="checkbox"
                            aria-label={`Select ${row.original.name}`}
                            className="relative z-10 text-teal-600 rounded-sm border-slate-300 focus:ring-0"
                            checked={row.original.isSelected}
                            onClick={(e) => {
                                e.stopPropagation();
                                toggleOne(id);
                            }}
                            onChange={() => {}}
                        />
                    );
                },
            },

            {
                Header: "Client",
                accessor: "name",
                Cell: ({ row, value }) => (
                    <div
                        className="relative z-10"
                        onClickCapture={(e) => e.stopPropagation()}
                    >
                        <a
                            href={`/clients/manage/${row.original.id}`}
                            target="_blank"
                            rel="noreferrer"
                            className="font-medium text-slate-800"
                        >
                            {value}
                        </a>
                        {/* <div className="text-xs text-slate-500">
                            ID: {row.original.id}
                        </div> */}
                    </div>
                ),
            },
            /* { Header: "Email", accessor: "email" },
            { Header: "Manager", accessor: "manager" }, */
            {
                Header: "Current team",
                accessor: "currentTeamId",
                Cell: ({ value }) => (
                    <span className="inline-block px-2 py-1 text-xs rounded bg-slate-100 text-slate-700">
                        {currentTeamName(value)}
                    </span>
                ),
            },
            {
                Header: "New team",
                id: "newTeam",
                disableSortBy: true,
                Cell: ({ row }) => (
                    <div className="relative z-20 pointer-events-auto">
                        <TeamSelect
                            teams={teams}
                            value={row.original.targetTeamId}
                            onChange={(val) =>
                                updateClientTarget(row.original.id, val)
                            }
                            excludeId={teamIdToDelete}
                            placeholder={
                                noCandidates ? "No candidate teams" : "Select team…"
                            }
                            disabled={noCandidates}
                        />
                    </div>
                ),
            },
        ],
        [
            areAllVisibleSelected,
            toggleAllVisible,
            toggleOne,
            teams,
            teamIdToDelete,
            currentTeamName,
            updateClientTarget,
            noCandidates,
        ]
    );

    // Add bottom padding when the bulk bar is visible so it never overlaps the table
    const padForBulkBar = selected.size > 0 ? "pb-24" : "";

    return (
        <PageContainer
            isLoading={loading}
            loadingText="Loading page..."
            showOverlay={true}
        >
            <Toaster />
            <ConfirmationModal
                showDialog={confirmOpen}
                busy={submitting}
                onCancel={() => { if (!inFlight.current) setConfirmOpen(false); }}
                onConfirm={onConfirmDelete}
                title="Permanently delete Team?"
                description={`Delete ${teamName} permanently and reassign ${clients.length} affected clients and their affected subscriptions using the selected Teams. This cannot be undone.`}
            />
            <ReactTooltip
                type="light"
                effect="solid"
                place="top"
                border={true}
                borderColor={"rgb(220,220,220)"}
                className="rounded-md bg-gray-50"
                multiline={false}
            />
            <div className="flex h-screen overflow-hidden">
                <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
                <div className="relative flex flex-col flex-1 overflow-x-hidden overflow-y-auto">
                    <Header
                        sidebarOpen={sidebarOpen}
                        setSidebarOpen={setSidebarOpen}
                    />
                    <main>
                        <div
                            className={`w-full px-4 py-8 mx-auto sm:px-6 lg:px-8 max-w-9xl ${padForBulkBar}`}
                        >
                            <div className="flex flex-col items-start w-full">
                                <Breadcrumbs
                                    home="/teams"
                                    pages={[
                                        {
                                            name: "Teams",
                                            href: `/teams`,
                                            current: false,
                                        },
                                        {
                                            name: "Delete Team",
                                            href: "#",
                                            current: true,
                                        },
                                    ]}
                                />

                                <h1 className="p-4 text-2xl font-bold md:text-3xl text-slate-800">
                                    Delete Team – Reassign Clients
                                </h1>
                                {error && <div role="alert" className="w-full p-4 mb-4 text-sm border border-red-200 rounded-sm bg-red-50 text-red-700">
                                    <p>{error}</p>
                                    {deletionNeedsReview && <div className="mt-2">
                                        <p>Refresh Teams and verify affected assignments before attempting another deletion. A missing Team alone does not confirm success.</p>
                                        <button type="button" onClick={() => navigate("/teams")} className="btn mt-3 bg-white">Refresh Teams</button>
                                    </div>}
                                    {errorStatus === 401 && <a href="/" className="inline-block mt-2 underline">Go to sign in</a>}
                                    <button type="button" disabled={submitting} onClick={() => setPreviewAttempt((value) => value + 1)} className="btn mt-3 bg-white">Reload preview</button>
                                </div>}
                                {(!canReadTeams || !canDeleteTeams) && (
                                    <div className="w-full p-4 mb-4 text-sm border rounded-sm border-slate-200 text-slate-600">
                                        Team deletion requires teams.read and teams.delete.
                                    </div>
                                )}

                                <fieldset disabled={!previewReady || submitting || !canReadTeams || !canDeleteTeams} className="w-full min-w-0">
                                <div className="w-full p-4 mb-4 text-sm border rounded-sm bg-slate-50 text-slate-600 border-slate-200">
                                    <div>
                                        <span className="font-semibold text-slate-800">
                                            Team Name:
                                        </span>{" "}
                                        <span className="text-slate-700">
                                            {teamName || "—"}
                                        </span>
                                    </div>
                                    <div className="mt-1">
                                        <span className="font-semibold text-slate-800">
                                            Department:
                                        </span>{" "}
                                        <span className="text-slate-700">
                                            {departmentName || "—"}
                                        </span>
                                    </div>
                                    <div className="mt-1">
                                        <span className="font-semibold text-slate-800">
                                            Affected clients:
                                        </span>{" "}
                                        <span className="text-slate-700">
                                            {clients.length}
                                        </span>
                                    </div>
                                    {noCandidates && clients.length > 0 && (
                                        <div className="inline-block px-2 py-1 mt-2 border rounded text-amber-700 bg-amber-50 border-amber-200">
                                            No candidate teams available in this
                                            department. Create or select a different
                                            team before continuing.
                                        </div>
                                    )}
                                </div>

                                <div className="w-full p-4 mb-6 border border-red-200 rounded-sm bg-red-50">
                                    <p className="text-sm text-red-700">
                                        {/*  <span className="font-semibold">
                                            {teamName || "—"}
                                        </span> */}
                                        Team deletion is permanent. Assign a replacement for all
                                        affected clients before the{" "}
                                        <span className="font-semibold">
                                            "Confirm Deletion"
                                        </span>{" "}
                                        button becomes active.
                                    </p>
                                </div>

                                <div className="relative z-0 w-full p-4 bg-white border rounded-sm shadow border-slate-200">
                                    {loading ? (
                                        <div className="py-10 text-center text-slate-500">
                                            Loading…
                                        </div>
                                    ) : !previewReady && clients.length === 0 ? (
                                        <div className="py-10 text-center text-slate-500">Reassignment details must be available before this Team can be deleted.</div>
                                    ) : clients.length === 0 ? (
                                        <div className="py-10 text-center text-slate-500">
                                            The server preview reports no affected clients.
                                        </div>
                                    ) : (
                                        <ReassignTeamTable
                                            columns={columns}
                                            data={tableData}
                                            onVisibleRowsChange={setVisibleRows}
                                        />
                                    )}
                                </div>

                                <BulkActionBar
                                    count={selected.size}
                                    teams={teams}
                                    targetId={bulkTarget}
                                    setTargetId={setBulkTarget}
                                    onApply={applyBulk}
                                    disabled={loading || submitting || !previewReady || !canDeleteTeams}
                                />

                                <div className="flex flex-col items-end w-full gap-3 mt-8 sm:flex-row sm:justify-end">
                                    <span
                                        data-tip={
                                            canDeleteTeams
                                                ? "Confirm deletion"
                                                : noPermissionMessage
                                        }
                                        className="inline-flex"
                                    >
                                        <button
                                            type="button"
                                            onClick={() => { if (canSubmit) setConfirmOpen(true); }}
                                            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-teal-500 rounded-sm shadow-sm hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-teal-500"
                                            disabled={!canSubmit}
                                            title={
                                                loading
                                                    ? "Loading…"
                                                    : clients.length > 0 &&
                                                      stillInDeletedTeam
                                                    ? "Reassign all clients first"
                                                    : ""
                                            }
                                        >
                                            {submitting ? "Processing..." : "Confirm deletion"}
                                        </button>
                                    </span>
                                </div>
                                </fieldset>
                            </div>
                        </div>
                    </main>
                </div>
            </div>
        </PageContainer>
    );
};

export default TeamsReassignOnDeletePage;
